import { FC, ReactElement, useId, type JSX } from 'react';

import type { RadioGroupItemProps } from '@radix-ui/react-radio-group';
import reactToText from 'react-to-text';

import { childrenRecipe, labelStyle, radioTileRadioStyle, tileWrapperStyle } from './RadioTile.css';
import { Image as SparkyImage } from '../../Image/Image';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';
import { RadioBase } from '../RadioBase';
import { useRadioGroup } from '../RadioGroupContext';
import { RadioIndicator } from '../RadioIndicator';

type ImageProps = Pick<
  Parameters<typeof SparkyImage>[0],
  'alt' | 'height' | 'maxHeight' | 'maxWidth' | 'src' | 'width'
>;

const Image: FC<ImageProps> = ({ ...props }) => <SparkyImage {...props} objectFit="contain"></SparkyImage>;

type RadioTileProps = Pick<RadioGroupItemProps, 'value'> & {
  alignLabel?: 'center' | 'start';
  children: ReactElement<typeof Image> | string;
  icon?: JSX.Element;
  subtitle?: string;
};

type RadioTileCompoundButtonProps = {
  Image: typeof Image;
};

export const RadioTile: FC<RadioTileProps> & RadioTileCompoundButtonProps = ({
  alignLabel = 'start',
  children,
  value,
  icon,
  subtitle,
}) => {
  const id = useId();
  const { ariaDescribedby } = useRadioGroup();

  return (
    <label className={labelStyle} data-label={reactToText(children)}>
      <div className={tileWrapperStyle}>
        <RadioBase
          className={radioTileRadioStyle}
          aria-label={reactToText(children)}
          aria-describedby={ariaDescribedby}
          id={id}
          value={value}>
          <RadioIndicator />
        </RadioBase>
        {icon ? <Stack.Item shrink={false}>{icon}</Stack.Item> : null}
        <div className={childrenRecipe({ alignLabel })}>
          <Text weight="bold">{children}</Text>
          {subtitle && (
            <Text weight="regular" size="BodyM">
              {subtitle}
            </Text>
          )}
        </div>
      </div>
    </label>
  );
};

RadioTile.Image = Image;
RadioTile.Image.displayName = 'RadioTile.Image';
